import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../providers/ToastProvider';
import { patchDmMessageTriage, type DmMessageTriagePatch } from '../services/dmMessagesInbox';
import {
    applyOptimisticTriage,
    restoreTriage,
    snapshotTriage,
    triageQueryKeys
} from './dmMessageTriageCache';
import { triageFailureMessage } from './dmMessageTriageErrors';

interface PatchTriageVariables {
    patientId: string;
    patch: DmMessageTriagePatch;
}

// Optimistic in every cached list and view; a failure rolls back, and the settle refetch catches up.
export function usePatchDmMessageTriage() {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    return useMutation({
        mutationFn: ({ patientId, patch }: PatchTriageVariables) =>
            patchDmMessageTriage(patientId, patch),
        onMutate: async ({ patientId, patch }) => {
            const keys = triageQueryKeys(patientId);
            await Promise.all(keys.map((queryKey) => queryClient.cancelQueries({ queryKey })));
            const snapshot = snapshotTriage(queryClient, patientId);
            applyOptimisticTriage(queryClient, patientId, patch);
            return { snapshot };
        },
        onError: (error, { patch }, context) => {
            if (context) restoreTriage(queryClient, context.snapshot);
            showToast(triageFailureMessage(error, patch), 'error');
        },
        onSettled: (_data, _error, { patientId }) =>
            Promise.all(
                triageQueryKeys(patientId).map((queryKey) =>
                    queryClient.invalidateQueries({ queryKey })
                )
            )
    });
}
